import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Router} from '@angular/router';
import {MatButtonModule} from '@angular/material/button';
import {MessageCard} from '../messenger.models';

/** Each kind's glyph (carbon messenger.md, Cards). */
const CARD_ICON: Readonly<Record<MessageCard['kind'], string>> = {
  meeting: 'fa-solid fa-video',
  event: 'fa-regular fa-calendar',
  show: 'fa-solid fa-clapperboard-play',
  mission: 'fa-solid fa-rocket'
};

let nextCardId = 0;

/**
 * A MESSAGE CARD (ITP-E-194-10, B-20), after carbon's `.itp-msgr-card`: a message whose content is a thing with an
 * action — a meeting, a calendar event, a Show or a mission. The glyph tile, the title, one meta line (a live meeting
 * leads it with the green dot), then the card's action as a real button. Call cards are ITP-P-34's and never this.
 * Where the card stands alone in the thread, with no bubble (ITP-E-240-06), a header line above its head says who did
 * what ("Ben Member started a meeting"); a card given none renders as before.
 */
@Component({
  standalone: true,
  selector: 'messenger-message-card',
  imports: [CommonModule, MatButtonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './messenger-message-card.component.html',
  styleUrls: ['./messenger-message-card.component.scss']
})
export class MessengerMessageCardComponent {
  @Input() card: MessageCard;
  /** One quiet line above the card's head, "<Name> started a meeting" (ITP-E-240-06); none when null or empty. */
  @Input() header: string | null = null;

  readonly titleId = `messenger-card-title-${nextCardId++}`;

  constructor(private router: Router) {
  }

  get icon(): string {
    return CARD_ICON[this.card?.kind] || 'fa-solid fa-square';
  }

  /** The action's link: another site opens in a new tab, a route of the app in place. */
  open(): void {
    const url = this.card?.action?.url;
    if (!url) {
      return;
    }
    if (/^https?:\/\//i.test(url)) {
      window.open(url, '_blank', 'noopener');
    } else {
      this.router.navigateByUrl(url).then();
    }
  }
}
